import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, HeartIcon, ArrowTrendingDownIcon, GlobeEuropeAfricaIcon, ShieldCheckIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const NaturalFarming = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const approachItems = [
        "Soil Regeneration & Microbial Activation",
        "Natural Nutrient Formulations",
        "Bio-Based Pest & Disease Management",
        "Crop Diversity & Ecological Balance",
        "Residue-Free Production Practices"
    ];

    const benefits = [
        { title: "Improves long-term soil fertility", icon: SparklesIcon },
        { title: "Reduces input cost", icon: ArrowTrendingDownIcon },
        { title: "Enhances crop quality", icon: ShieldCheckIcon },
        { title: "Promotes environmental sustainability", icon: GlobeEuropeAfricaIcon },
        { title: "Supports healthy and safe food production", icon: HeartIcon }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/newimages/mas website natural farming.webp')}
                        alt="Natural Farming"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Natural <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Farming</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Farming in Harmony with Nature.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-1/2"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            <strong className="text-gray-900 font-bold">Natural Farming</strong> at Marvelous Agri Solution is built on the principle of restoring soil life, strengthening crop immunity, and reducing chemical dependency.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            We promote a sustainable cultivation system that enhances productivity while protecting soil health, biodiversity, and long-term farm profitability.
                        </p>

                        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl mb-12">
                            <p className="text-xl font-medium text-emerald-900 italic">
                                This is agriculture aligned with nature — not against it.
                            </p>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            Our Natural Farming Approach
                        </h3>

                        <div className="space-y-4 mb-8">
                            {approachItems.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium text-lg">{item}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-emerald-800 font-medium italic mb-10 border-b border-gray-200 pb-6">
                            Every practice is designed to improve soil structure, increase organic matter, and enhance natural resilience.
                        </p>

                    </motion.div>

                    <div className="lg:w-1/2 relative space-y-12">
                        <motion.img
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            src={require('../../../assets/newimages/mas website natural farmin g.webp')}
                            alt="Natural Farming Process"
                            className="rounded-3xl w-full h-64 object-cover shadow-xl mb-8"
                        />

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-8">
                                Why Natural Farming Matters
                            </h3>

                            <div className="space-y-6 mb-8">
                                {benefits.map((benefit, idx) => (
                                    <div key={idx} className="flex items-center gap-4">
                                        <div className="bg-emerald-100 p-3 rounded-xl flex-shrink-0">
                                            <benefit.icon className="w-6 h-6 text-emerald-600" />
                                        </div>
                                        <p className="text-gray-800 font-medium text-lg">{benefit.title}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-gray-50 border-t-2 border-emerald-500 p-6 rounded-b-xl -mx-8 -mb-8 mt-8">
                                <p className="font-bold text-emerald-800 text-center italic">
                                    Natural systems build stronger crops from the roots up.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="bg-gray-900 p-10 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                        >
                            <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl text-white"></div>

                            <h3 className="text-3xl font-bold mb-8 flex items-center gap-3 relative z-10 text-emerald-400">
                                Sustainable Productivity for the Future
                            </h3>

                            <div className="relative z-10 space-y-6">
                                <p className="text-xl font-light text-gray-300 leading-relaxed">
                                    Natural Farming is <span className="font-medium text-white">not</span> about reducing yield — it is about <span className="font-medium text-white border-b border-emerald-500">building sustainable yield</span>.
                                </p>

                                <p className="text-lg font-light text-gray-400 italic">
                                    At Marvelous Agri Solution, we combine traditional wisdom with scientific validation to create a balanced, eco-friendly farming model.
                                </p>
                            </div>

                        </motion.div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default NaturalFarming;
